/**
 * E002-T018: StreamLainSidebarSetup
 * sl-oll9.13: SRP — only sidebar tab + fullscreen view registration.
 * Event routing extracted to StreamLainEventRouter.
 */

import { I18n } from '@affine/i18n';
import {
  AttachmentIcon,
  DatabaseTableViewIcon,
  DeleteIcon,
  PlayIcon,
  SettingsIcon,
} from '@blocksuite/icons/rc';
import { Service } from '@toeverything/infra';
import React, { type ComponentType, type FC, lazy, Suspense } from 'react';

import { DatabaseFullscreenSwitchIcon } from '../../../blocksuite/block-suite-mode-switch/database-fullscreen-switch-icon';
import elsterIconWhiteUrl from '../../../desktop/dialogs/setting/workspace-setting/integration/erica/elster-icon-white.assets.svg';
import type { ExtensionSidebarService } from '../../workbench';
import { ensureBuiltInViewComponentsRegistered } from '../components/view-components/ensure-built-in-components';
import { isElsterEnabledFromBody } from '../hooks/use-elster-gate';
import { isNewsPortalEnabledFromBody } from '../hooks/use-news-portal-gate';
import { DOC_TRASH_SIDEBAR_TAB_ID } from '../sidebar/doc-trash/doc-trash-tab';
import { findBlockByFlavour } from '../utils/block-helpers';
import { findViewConfigBlockId } from '../utils/find-view-config-block-id';
import { ComponentRegistry } from './component-registry';
import { FullscreenRegistry } from './fullscreen-registry';

/** Keep registration synchronous; load a panel only when its view renders. */
function lazyView<Props extends object>(
  load: () => Promise<ComponentType<Props>>
): FC<Props> {
  const View = lazy(() =>
    load().then(component => ({ default: component }))
  ) as ComponentType<Props>;
  return function DeferredView(props: Props) {
    return (
      <Suspense fallback={null}>
        <View {...props} />
      </Suspense>
    );
  };
}

const ViewConfigFullscreenShell = lazyView(() =>
  import('../components/view-config-editor/shell').then(
    module => module.ViewConfigFullscreenShell
  )
);
const AutomationConfigPanelGated = lazyView(() =>
  import('../sidebar/automation-panel-gated').then(
    module => module.AutomationConfigPanelGated
  )
);
const WorkspaceDatabasesPanel = lazyView(() =>
  import('../sidebar/workspace-databases-panel').then(
    module => module.WorkspaceDatabasesPanel
  )
);
const AttachmentFullscreenView = lazyView(() =>
  import('../sidebar/attachment-fullscreen-view').then(
    module => module.AttachmentFullscreenView
  )
);
const DatabaseFullscreenView = lazyView(() =>
  import('../sidebar/database-fullscreen-view').then(
    module => module.DatabaseFullscreenView
  )
);
const DocTrashPanel = lazyView(() =>
  import('../sidebar/doc-trash/doc-trash-panel').then(
    module => module.DocTrashPanel
  )
);
const ElsterFullscreenView = lazyView(() =>
  import('../sidebar/elster-fullscreen-view').then(
    module => module.ElsterFullscreenView
  )
);
const NewsPortalFullscreenView = lazyView(() =>
  import('../sidebar/news-portal-fullscreen-view').then(
    module => module.NewsPortalFullscreenView
  )
);

/**
 * sl-djze.3: Custom event name for navigating to a pending decision in the review panel.
 * Dispatched from PendingDecisionNotificationItem in the notification list.
 */
export const PENDING_DECISION_REVIEW_EVENT = 'pending-decision-review';

export interface PendingDecisionReviewDetail {
  pendingDecisionId: string;
}

/**
 * Registers StreamLain sidebar tabs and fullscreen views.
 * No event listener logic — that lives in StreamLainEventRouter.
 */
export class StreamLainSidebarSetup extends Service {
  constructor(
    private readonly extensionSidebarService: ExtensionSidebarService,
    private readonly fullscreenRegistry: FullscreenRegistry,
    private readonly componentRegistry: ComponentRegistry
  ) {
    super();

    // sl-9qm.4.3 / sl-2q8e.8: populate ComponentRegistry on workspace boot
    // so AppBase editor shell + renderer have view-components available.
    ensureBuiltInViewComponentsRegistered(this.componentRegistry);

    // E018-S010-T005: Register Workspace Databases tab (replaces old Tables tab)
    this.extensionSidebarService.register({
      id: 'streamlain-workspace-databases',
      label: I18n['com.streamlain.streamlain.workspace.databases'](),
      icon: React.createElement(DatabaseFullscreenSwitchIcon),
      component: WorkspaceDatabasesPanel,
      order: 45,
    });

    // Register Automation tab
    // sl-n8i1.5: Uses gated panel that shows placeholder when workflow engine not configured
    this.extensionSidebarService.register({
      id: 'streamlain-automation',
      label: I18n['com.streamlain.streamlain.workspace.automations'](),
      icon: React.createElement(PlayIcon),
      component: AutomationConfigPanelGated,
      order: 50, // After Workspace Databases, before Chat
    });

    // The doc's trash: every whole block deleted in this doc, 30 days.
    this.extensionSidebarService.register({
      id: DOC_TRASH_SIDEBAR_TAB_ID,
      label: I18n['com.streamlain.trash.tab'](),
      icon: React.createElement(DeleteIcon),
      component: DocTrashPanel,
      order: 55,
    });

    // sl-yphr.14: Register ELSTER fullscreen view
    this.fullscreenRegistry.register({
      id: 'elster',
      icon: React.createElement('img', {
        src: elsterIconWhiteUrl,
        width: 16,
        height: 16,
        alt: 'ELSTER',
        style: { display: 'inline-block' },
      }),
      label: 'ELSTER',
      shortcut: 'Alt+T',
      component: ElsterFullscreenView,
      isAvailable: isElsterEnabledFromBody,
      findDefaultBlockId: findBlockByFlavour('affine:elster'),
      order: 30,
    });

    // sl-oll9.26: Register News Portal fullscreen view
    this.fullscreenRegistry.register({
      id: 'news-portal',
      icon: React.createElement(DatabaseTableViewIcon),
      label: 'News Portal',
      shortcut: 'Alt+N',
      component: NewsPortalFullscreenView,
      isAvailable: isNewsPortalEnabledFromBody,
      findDefaultBlockId: () => null,
      order: 26,
    });

    // sl-yphr.18: Register Database fullscreen view
    this.fullscreenRegistry.register({
      id: 'database',
      icon: React.createElement(DatabaseTableViewIcon),
      label: 'Database',
      shortcut: 'Alt+D',
      component: DatabaseFullscreenView,
      isAvailable: () => true,
      findDefaultBlockId: findBlockByFlavour('affine:database'),
      order: 10,
    });

    this.fullscreenRegistry.register({
      id: 'attachment',
      icon: React.createElement(AttachmentIcon),
      label: 'Attachment',
      component: AttachmentFullscreenView,
      hideModalClose: true,
      isAvailable: () => true,
      findDefaultBlockId: findBlockByFlavour('affine:attachment'),
      order: 12,
    });

    // sl-8n5m.5.1: Register ViewConfig fullscreen view. The button is
    // always available; findDefaultBlockId returns null when no ViewConfig
    // database exists yet — the shell's empty state (sl-8n5m.5.2) handles
    // the create-on-demand flow.
    this.fullscreenRegistry.register({
      id: 'view-config',
      icon: React.createElement(SettingsIcon),
      label: 'App configuration',
      shortcut: 'Alt+V',
      component: ViewConfigFullscreenShell,
      isAvailable: () => true,
      findDefaultBlockId: findViewConfigBlockId,
      order: 30,
    });
  }
}
